/**
 * Aufzeichnung fuer Papierkorb und Rueckgaengig (F-45).
 *
 * Eine Dexie-Middleware unterhalb aller Repositories, nach dem Muster der
 * Sync-Erfassung (`db/sync/erfassung.ts`): In einer MARKIERTEN Transaktion
 * haelt sie von jedem Schreibvorgang auf eine Datentabelle den Stand vorher
 * und nachher fest. So landen alle Nebenwirkungen einer Loeschung (Aufgaben,
 * die vom Projekt geloest werden, Terminbestaetigungen, Zeitbloecke ...)
 * automatisch im selben Papierkorb-Eintrag - kein Repository muss sie
 * aufzaehlen.
 *
 * Ohne Markierung geht jede Anfrage unveraendert durch und kostet nichts.
 * Verschachtelte Dexie-Transaktionen teilen dieselbe IDB-Transaktion und
 * damit dieselbe Markierung.
 */
import Dexie, { type DBCore, type DBCoreMutateResponse, type DBCoreTable, type Middleware, type Transaction } from 'dexie';

import { betroffeneSchluessel } from '@/db/sync/erfassung';
import type { RohAenderung } from '@/domain/papierkorb/verdichten';
import type { AktionsMeldung } from '@/domain/papierkorb/typen';
import type { Daten } from '@/domain/sync/zusammenfuehren';

const MARKE = '__olePrivatAufzeichnung';

/** Je Datenbank, nur im Speicher. */
export interface AufzeichnungsZustand {
  /**
   * Wird nach dem Commit jeder aufgezeichneten Aktion aufgerufen - die
   * Rueckgaengig-Meldung haengt sich hier ein. Ohne Aenderungen kein Aufruf.
   */
  beiAktion: ((m: AktionsMeldung) => void) | null;
}

export const neueAufzeichnung = (): AufzeichnungsZustand => ({ beiAktion: null });

const markeVon = (idbtrans: unknown): RohAenderung[] | undefined =>
  (idbtrans as Record<string, RohAenderung[] | undefined> | null | undefined)?.[MARKE];

/** Ab jetzt zeichnet diese Transaktion (samt verschachtelten) auf. */
export function aufzeichnungStarten(tx: Transaction): RohAenderung[] {
  const roh: RohAenderung[] = [];
  (tx.idbtrans as unknown as Record<string, unknown>)[MARKE] = roh;
  return roh;
}

/** Laeuft in der aktuellen Transaktion von `db` schon eine Aufzeichnung? */
export function laufendeAufzeichnung(db: Dexie): RohAenderung[] | undefined {
  const tx = Dexie.currentTransaction;
  return tx && tx.db === db ? markeVon(tx.idbtrans) : undefined;
}

/**
 * Bewusst als `.then`-Kette statt `async`/`await` (siehe erfassung.ts): Ein
 * natives `await` verliert den Transaktionskontext von Dexie.
 */
function aufgezeichnet(name: string, t: DBCoreTable): DBCoreTable {
  return {
    ...t,
    mutate(req): Promise<DBCoreMutateResponse> {
      const roh = markeVon(req.trans);
      if (!roh) return t.mutate(req);
      return betroffeneSchluessel(t, req).then((keys) => {
        if (!keys.length) return t.mutate(req);
        const alteHolen: Promise<(Daten | undefined)[]> = req.type === 'add'
          ? Dexie.Promise.resolve(keys.map(() => undefined))
          : t.getMany({ trans: req.trans, keys, cache: 'clone' });
        return alteHolen.then((alte) => t.mutate(req).then((res) => {
          const neue = req.type === 'add' || req.type === 'put' ? req.values : null;
          keys.forEach((k, i) => {
            if (res.failures?.[i]) return;
            const vorher = alte[i];
            const nachher = neue ? (structuredClone(neue[i]) as Daten | undefined) : undefined;
            if (!vorher && !nachher) return;
            const r: RohAenderung = { t: name, id: String(k) };
            if (vorher) r.vorher = vorher;
            if (nachher) r.nachher = nachher;
            roh.push(r);
          });
          return res;
        }));
      });
    },
  };
}

/** Nur die genannten Tabellen werden aufgezeichnet - nie der Papierkorb selbst. */
export function aufzeichnung(tabellen: ReadonlySet<string>): Middleware<DBCore> {
  return {
    stack: 'dbcore',
    name: 'aufzeichnung',
    create(down) {
      return {
        ...down,
        table(name) {
          const t = down.table(name);
          return tabellen.has(name) ? aufgezeichnet(name, t) : t;
        },
      };
    },
  };
}
